import { Eye, EyeOff } from "lucide-react";
import { type ComponentProps, type KeyboardEvent, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { LanguageSelect } from "@/components/language-select";
import { cn } from "@/lib/utils";

import { AuthError } from "@/auth/types";

/** Centered card for the sign-in screens (no admin menu). */
export function AuthLayout({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-dvh flex-col">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-bold">{t("app.name")}</span>
        <LanguageSelect />
      </div>
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-6 px-4 pb-16">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">{title}</h1>
          {intro ? <p className="text-muted-foreground">{intro}</p> : null}
        </header>
        {children}
      </main>
    </div>
  );
}

export const INPUT =
  "min-h-touch w-full rounded-md border bg-background px-3 text-base aria-[invalid=true]:border-status-problem";

interface FieldProps extends Omit<ComponentProps<"input">, "id"> {
  label: string;
  hint?: string;
  error?: string | null;
}

/** Labelled input for the auth screens (label + hint + error wired with aria). */
export function AuthField({ label, hint, error, className, ...props }: FieldProps) {
  const id = useId();
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(INPUT, className)}
        {...props}
      />
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-sm text-status-problem">
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface PasswordFieldProps extends Omit<FieldProps, "type"> {
  capsLockHint?: boolean;
}

/** Password input with show/hide and a Caps Lock warning. */
export function PasswordField({
  label,
  hint,
  error,
  capsLockHint = true,
  ...props
}: PasswordFieldProps) {
  const { t } = useTranslation("auth");
  const id = useId();
  const [visible, setVisible] = useState(false);
  const [caps, setCaps] = useState(false);
  const describedBy = [
    hint ? `${id}-hint` : null,
    caps ? `${id}-caps` : null,
    error ? `${id}-error` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (capsLockHint && typeof event.getModifierState === "function") {
      setCaps(event.getModifierState("CapsLock"));
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? "text" : "password"}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          onKeyUp={onKey}
          onKeyDown={onKey}
          className={cn(INPUT, "pr-12")}
          {...props}
        />
        <button
          type="button"
          onClick={() => {
            setVisible((v) => !v);
          }}
          aria-label={visible ? t("login.hide_password") : t("login.show_password")}
          aria-pressed={visible}
          className="absolute inset-y-0 right-0 inline-flex w-12 items-center justify-center text-muted-foreground"
        >
          {visible ? (
            <EyeOff className="size-5" aria-hidden />
          ) : (
            <Eye className="size-5" aria-hidden />
          )}
        </button>
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {caps ? (
        <p id={`${id}-caps`} role="status" className="text-xs font-medium text-[#92400e]">
          {t("login.caps_lock")}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-sm text-status-problem">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Plain-language text for an auth failure (no token/JWT/401 wording). */
export function useAuthErrorText(): (error: unknown) => string {
  const { t } = useTranslation("auth");
  return (error: unknown) => {
    if (error instanceof AuthError) {
      const minutes = error.retryAfter ? Math.max(1, Math.ceil(error.retryAfter / 60)) : null;
      if (error.key === "locked" && minutes) {
        return t("errors.locked_retry", { minutes });
      }
      if (error.key === "rate_limited" && minutes) {
        return t("errors.rate_limited_retry", { minutes });
      }
      return t(`errors.${error.key}`);
    }
    return t("errors.unknown");
  };
}

/** Only same-app paths may be returned to after sign-in (no open redirects). */
export function safeNext(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return "/";
  }
  if (/^\/(login|forgot|reset)(\/|\?|$)/.test(value)) {
    return "/";
  }
  return value;
}

export function FormAlert({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }
  return (
    <p
      role="alert"
      className="rounded-md border border-status-problem bg-[#fef2f2] p-3 text-sm text-[#991b1b]"
    >
      {message}
    </p>
  );
}
